import { AnimatePresence, motion } from 'framer-motion';
import type { Translate } from 'next-translate';
import React from 'react';
import { Container } from '@/components/elements/Container';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { TextWithLinks } from '@/components/elements/TextWithLinks';
import { ContentTypeMenu } from '@/components/landing/blocks/ContentTypeMenu';
import type { LandingMain, LandingMainTab, LandingMainTabId } from '@/types';
import { MainMedia } from '../MainMedia';
import styles from './MainAbout.module.scss';

interface Props {
	data: LandingMain['about'];
	t: Translate;
	localeHref: (path: string) => string;
	onExploreClick: () => void;
}

export const MainAbout: React.FC<Props> = ({
	data,
	t,
	localeHref,
	onExploreClick,
}) => {
	const [activeId, setActiveId] = React.useState<LandingMainTabId>(
		data.tabs[0].id,
	);
	const activeTab = data.tabs.find(tab => tab.id === activeId) ?? data.tabs[0];

	const renderDetails = (tab: LandingMainTab) => (
		<>
			<p className={styles['about__text']}>
				<TextWithLinks
					text={t(tab.text)}
					parts={Object.fromEntries(
						tab.links.map(link => [
							link.placeholder,
							<LinkWithQuery
								key={link.placeholder}
								href={localeHref(link.href)}
								className={styles['about__text-link']}
							>
								{t(link.text)}
							</LinkWithQuery>,
						]),
					)}
				/>
			</p>
			<LinkWithQuery
				href={`${localeHref('/tools')}?category=${tab.toolsCategory}`}
				className={styles['about__explore']}
				onClick={onExploreClick}
			>
				{t(tab.linkText)}&nbsp;→
			</LinkWithQuery>
		</>
	);

	return (
		<section className={styles['about']}>
			<Container>
				<h2 className={styles['about__title']}>{t(data.title)}</h2>

				<div className={styles['about__body']}>
					<ContentTypeMenu
						idPrefix='main-about'
						items={data.tabs.map(tab => ({
							id: tab.id,
							title: t(tab.title),
							content: renderDetails(tab),
						}))}
						activeId={activeId}
						onSelect={id => setActiveId(id as LandingMainTabId)}
						className={styles['about__menu']}
					/>

					<div className={styles['about__media']}>
						{/*
							A new key per tab remounts the clip, so it starts over from
							the moment of the switch and loops until the next one
						*/}
						<AnimatePresence initial={false}>
							<motion.div
								key={activeTab.id}
								className={styles['about__media-frame']}
								initial={{ opacity: 0, scale: 1.02 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.4, ease: 'easeOut' }}
							>
								<MainMedia
									media={activeTab.media}
									alt={t(activeTab.media.alt)}
									sizes='(max-width: 767px) 100vw, 755px'
									className={styles['about__media-item']}
								/>
							</motion.div>
						</AnimatePresence>
					</div>

					{/* Phones: the open tab's copy sits under the picture instead */}
					<div className={styles['about__details']}>
						<AnimatePresence mode='wait' initial={false}>
							<motion.div
								key={activeTab.id}
								initial={{ opacity: 0, y: 8 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -8 }}
								transition={{ duration: 0.2, ease: 'easeOut' }}
							>
								{renderDetails(activeTab)}
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</Container>
		</section>
	);
};
